'use client';

import './tooltip.scss';

import * as RdxTooltip from '@radix-ui/react-tooltip';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { cn, isOnDesktop } from '../../utils/helpers';

const enableClicks = !isOnDesktop();

/**
 * Tooltip Global Provider.
 */
interface TooltipProviderProps extends React.PropsWithChildren {
  delayDuration?: RdxTooltip.TooltipProviderProps['delayDuration'];
  skipDelayDuration?: RdxTooltip.TooltipProviderProps['skipDelayDuration'];
  disableHoverableContent?: RdxTooltip.TooltipProviderProps['disableHoverableContent'];
}
export const TooltipProvider: React.FC<TooltipProviderProps> = ({
  children,
  delayDuration = 300,
  disableHoverableContent,
  skipDelayDuration,
}) => {
  return (
    <RdxTooltip.Provider
      delayDuration={enableClicks ? 0 : delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      {children}
    </RdxTooltip.Provider>
  );
};

/**
 * Trigger Trigger Context
 */
interface TooltipTriggerContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  isOpen: false,
  setIsOpen: () => {},
});
const ToolTipTriggerProvider = TooltipTriggerContext.Provider;

/**
 * Tooltip
 */
export interface TooltipProps extends React.HTMLAttributes<HTMLElement> {
  isDefaultOpen?: RdxTooltip.TooltipProps['defaultOpen'];
  isOpen?: RdxTooltip.TooltipProps['open'];
  onOpenChange?: RdxTooltip.TooltipProps['onOpenChange'];
}

export const Tooltip = ({
  children,
  isDefaultOpen = undefined,
  isOpen = false,
  onOpenChange,
  ...props
}: TooltipProps) => {
  const [openState, setIsOpen] = useState(isOpen);
  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);
  return (
    <RdxTooltip.Root
      defaultOpen={isDefaultOpen}
      open={openState}
      onOpenChange={(newOpenState) => {
        setIsOpen(newOpenState);
        if (onOpenChange) {
          onOpenChange(newOpenState);
        }
      }}
      {...props}
    >
      <ToolTipTriggerProvider value={{ isOpen: !!openState, setIsOpen }}>
        {children}
      </ToolTipTriggerProvider>
    </RdxTooltip.Root>
  );
};

export type TooltipTriggerProps = RdxTooltip.TooltipTriggerProps;

export const TooltipTrigger = ({ children }: TooltipTriggerProps) => {
  const { isOpen, setIsOpen } = useContext(TooltipTriggerContext);
  return (
    <RdxTooltip.Trigger
      asChild
      className="Tooltip__trigger"
      onClick={(e) => {
        if (!enableClicks) {
          return;
        }
        e.preventDefault();
      }}
      onTouchStart={(e) => {
        if (!enableClicks) {
          return;
        }
        e.preventDefault();
        setIsOpen(!isOpen);
      }}
    >
      {children}
    </RdxTooltip.Trigger>
  );
};

export interface TooltipContentProps extends RdxTooltip.TooltipContentProps {
  fitContent?: boolean;
  size?: 'sm' | 'md' | 'lg';
  usePortal?: boolean;
}

export const TooltipContent = ({
  align = 'end',
  side = 'top',
  size,
  alignOffset = -5,
  sideOffset = 5,
  fitContent,
  usePortal,
  className,
  ...props
}: TooltipContentProps) => {
  const tooltip = (
    <RdxTooltip.Content
      {...props}
      asChild
      align={align}
      side={side}
      alignOffset={alignOffset}
      sideOffset={sideOffset}
    >
      <div
        className={cn('Tooltip__content', {
          [`${className}`]: !!className,
          [`Tooltip__content--${size}`]: !!size,
          'Tooltip__content--fit': fitContent,
        })}
      >
        {props.children}
        <RdxTooltip.Arrow
          height={6}
          width={12}
          offset={20}
          className="Tooltip__arrow"
        />
      </div>
    </RdxTooltip.Content>
  );

  if (usePortal) {
    return <RdxTooltip.Portal>{tooltip}</RdxTooltip.Portal>;
  }

  return tooltip;
};
